import React, { useState, useEffect, useContext } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../../api/axiosConfig";
import "./UserDetails.css";
import { ProjectContext } from "../../App";
import { IoMdClose } from "react-icons/io";
import { useAuth } from "../Authentication/AuthContext";
import { useRolePermissions } from "../Settings/RoleContext";

const UserDetails = () => {
  const { isNavBarOpen } = useContext(ProjectContext);
  const { id: userID } = useParams();
  const [user, setUser] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const { userDetails } = useAuth();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [updatedUser, setUpdatedUser] = useState(null);
  const [pictureDial, setPictureDial] = useState(false);
  const [selectedPicture, setSelectedPicture] = useState(null);
  const [previewPicture, setPreviewPicture] = useState(null);
  const { hasPermission } = useRolePermissions();
  const [performance, setPerformance] = useState({});
  const endpoint =
    userDetails.role_name === "Administrator" ||
    userDetails.role_name === "Super Admin"
      ? `/api/settings/users/${userID}`
      : `/api/settings/users/${userDetails?.id}`;

  const performanceEndPoint =
    userDetails.role_name === "Administrator" ||
    userDetails.role_name === "Super Admin"
      ? `/api/userspage/${userID}/performance`
      : `/api/userspage/${userDetails?.id}/performance`;
  const groupByProject = (data) => {
    const grouped = {};
    data.forEach((item) => {
      if (!grouped[item.project_name]) {
        grouped[item.project_name] = { logs: [], totalProjectHours: 0 };
      }
      grouped[item.project_name].logs.push(item);
      grouped[item.project_name].totalProjectHours += parseFloat(
        item.total_hours
      );
    });
    return grouped;
  };
  const fetchData = async () => {
    try {
      const response = await api.get(endpoint);
      const projectResponse = await api.get("/api/projects");
      const performanceResponse = await api.get(performanceEndPoint);
      setPerformance(groupByProject(performanceResponse.data));
      setProjects(projectResponse.data);
      setUser(response.data[0]); // Assuming response is an array with a single user
      setSelectedProjects(response.data[0].projects.map((p) => p.project_id));
      setUpdatedUser(response.data[0]); // Clone data for editing
      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setError(error);
      setLoading(false);
    }
  };
  useEffect(() => {
    if (userDetails?.id || userID) {
      fetchData();
    }
  }, [userDetails?.id, userID, userDetails.role_name]);
  const handleProjectCheckboxChange = (e, projectId) => {
    setSelectedProjects((prevSelected) => {
      if (prevSelected.includes(projectId)) {
        return prevSelected.filter((id) => id !== projectId);
      } else {
        return [...prevSelected, projectId];
      }
    });
  };

  const handleBulkUpdate = async (userId, selectedProjects) => {
    try {
      await api.post("/api/bulk-update-projects", {
        userId: userId,
        projectIds: selectedProjects,
      }); // Update user details in the backend
      setIsEditing(false);
      fetchData();
      alert("User details updated successfully.");
    } catch (error) {
      console.error("Error updating user details:", error);
      alert("Failed to update user details.");
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUpdatedUser((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    const endpoint = `/api/settings/users/${user.id}`;
    try {
      await api.put(endpoint, updatedUser); // Update user details in the backend
      setUser(updatedUser); // Update the user state with new data
      setIsEditing(false);
      alert("User details updated successfully.");
    } catch (error) {
      console.error("Error updating user details:", error);
      alert("Failed to update user details.");
    }
  };
  const handleUploadPicture = async (e) => {
    e.preventDefault();
    if (!selectedPicture) {
      alert("Please select a picture to upload.");
      return;
    }

    const formData = new FormData();
    formData.append("profilePicture", selectedPicture);
    const endpoint =
      userDetails.role_name === "Administrator" ||
      userDetails.role_name === "Super Admin"
        ? `/api/users/${userID}/upload-picture`
        : `/api/users/${userDetails.id}/upload-picture`;
    try {
      const response = await api.post(endpoint, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.status === 200) {
        alert("Profile picture uploaded successfully.");
        setPictureDial(false);
        setSelectedPicture(null);
        setPreviewPicture(null);
        window.location.reload();
        // Optionally refresh user details to display the new picture
      } else {
        throw new Error("Failed to upload the profile picture.");
      }
    } catch (error) {
      alert(`Error: ${error.message}`);
    }
  };
  const handlePictureChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedPicture(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewPicture(reader.result); // Display the image preview
      };
      reader.readAsDataURL(file);
    }
  };
  if (loading)
    return (
      <div
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        }}
      >
        <span className="loading-animation">LOADING...</span>
      </div>
    );
  if (error) return <p>Error loading user details.</p>;

  return (
    <div
      className={
        isNavBarOpen
          ? "user-p-details-container"
          : "user-p-details-container-close"
      }
    >
      <h2>User Details</h2>
      <Link
        to={
          userDetails.role_name === "Administrator" ||
          userDetails.role_name === "Super Admin"
            ? "/settings"
            : "/home"
        }
      >
        <IoMdClose className="user-details-exit-arrow" />
      </Link>
      {user && (
        <div className="user-details">
          {user.picture && user.picture !== "null" ? (
            <img
              src={
                user.picture
                  ? `${api.defaults.baseURL}${user.picture}`
                  : "/default-avatar.png"
              }
              alt={`${user.name}'s Profile`}
              style={{
                width: "150px",
                height: "150px",
                borderRadius: "50%",
                marginBottom: "20px",
              }}
              onClick={() => setPictureDial((prev) => !prev)}
              title="Change Profile Picture"
            />
          ) : (
            <img
              alt="profile-picture"
              src="/uploads/profile-pictures/default.png"
              onClick={() => setPictureDial((prev) => !prev)}
              title="Add Profile Picture"
            />
          )}
          <table className="user-details-table">
            <tbody>
              <tr>
                <td style={{ fontWeight: "bold", width: "150px" }}>Name:</td>
                <td>{user.name}</td>
              </tr>
              <tr>
                <td style={{ fontWeight: "bold" }}>Email:</td>
                <td>
                  {isEditing ? (
                    <input
                      type="email"
                      name="email"
                      value={updatedUser.email}
                      onChange={handleInputChange}
                      className="user-details-element"
                      placeholder="User Email"
                    />
                  ) : (
                    user.email
                  )}
                </td>
              </tr>
              <tr>
                <td style={{ fontWeight: "bold" }}>Designation:</td>
                <td>
                  {isEditing && hasPermission("users", "update") ? (
                    <input
                      type="email"
                      name="designation"
                      value={updatedUser.designation}
                      onChange={handleInputChange}
                      className="user-details-element"
                      placeholder="User Email"
                    />
                  ) : (
                    user.designation
                  )}
                </td>
              </tr>
              <tr>
                <td style={{ fontWeight: "bold" }}>Employee Code:</td>
                <td>{user.employeecode}</td>
              </tr>
              <tr>
                <td style={{ fontWeight: "bold" }}>Gender:</td>
                <td>
                  {isEditing ? (
                    <select
                      name="gender"
                      value={updatedUser.gender}
                      onChange={handleInputChange}
                      className="gender-dropdown"
                    >
                      <option value="" disabled>
                        Select Gender
                      </option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  ) : (
                    user.gender
                  )}
                </td>
              </tr>
              <tr>
                <td style={{ fontWeight: "bold" }}>Phone:</td>
                <td>
                  {isEditing ? (
                    <input
                      type="tel"
                      name="phone"
                      value={updatedUser.phone}
                      onChange={handleInputChange}
                      className="user-details-element"
                      placeholder="User Phone Number"
                    />
                  ) : (
                    user.phone
                  )}
                </td>
              </tr>
              <tr>
                <td style={{ fontWeight: "bold" }}>Role:</td>
                <td>{user.role_name}</td>
              </tr>
              <tr>
                <td style={{ fontWeight: "bold" }}>Status:</td>
                <td>{user.status}</td>
              </tr>
              <tr>
                <td style={{ fontWeight: "bold" }}>Groups:</td>
                <td>
                  {user.groups.length > 0 ? (
                    <ul>
                      {user.groups.map((group, index) => (
                        <li key={index}>
                          {group.name} ({group.code}) - {group.description}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    "No groups assigned"
                  )}
                </td>
              </tr>
              <tr>
                <td style={{ fontWeight: "bold", verticalAlign: "top" }}>
                  Performance:
                </td>
                <td>
                  {performance && Object.keys(performance).length > 0 ? (
                    <div style={{ height: "20vh", overflow: "scroll" }}>
                      {Object.entries(performance).map(
                        ([projectName, data]) => (
                          <div key={projectName} className="user-performance">
                            <strong>Project : {projectName}</strong> – Total
                            Hours: <strong>{data.totalProjectHours}</strong>
                            {/* Group logs by phase_name */}
                            {Object.entries(
                              data.logs.reduce((acc, log) => {
                                if (!acc[log.phase_name]) {
                                  acc[log.phase_name] = [];
                                }
                                acc[log.phase_name].push(log);
                                return acc;
                              }, {})
                            ).map(([phaseName, logs]) => (
                              <div
                                key={phaseName}
                                style={{ marginTop: "10px" }}
                              >
                                <strong>Phase : {phaseName}</strong>
                                <ul
                                  style={{
                                    marginTop: "5px",
                                    marginBottom: "5px",
                                  }}
                                >
                                  {logs.map((log, index) => (
                                    <li
                                      key={index}
                                      style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        paddingLeft: "10px",
                                      }}
                                    >
                                      <p>{log.logtitle}</p>
                                      <p>{log.total_duration}</p>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ))}
                          </div>
                        )
                      )}
                    </div>
                  ) : (
                    "No performance data available"
                  )}
                </td>
              </tr>
              <tr>
                <td style={{ fontWeight: "bold" }}>Projects:</td>
                <td
                  style={{
                    display: "flex",
                    borderTop: "none",
                    borderLeft: "none",
                  }}
                >
                  {isEditing && hasPermission("projects", "delete") ? (
                    <div>
                      <p>All Projects :</p>
                      {projects.length > 0 ? (
                        <ul className="working-projects-ul">
                          {[...projects]
                            .sort((a, b) => {
                              const aSelected = selectedProjects.includes(
                                a.project_id
                              );
                              const bSelected = selectedProjects.includes(
                                b.project_id
                              );
                              return aSelected === bSelected
                                ? 0
                                : aSelected
                                ? -1
                                : 1;
                            })
                            .map((project, index) => (
                              <div style={{ display: "flex" }}>
                                <input
                                  style={{ cursor: "pointer" }}
                                  type="checkbox"
                                  checked={selectedProjects.includes(
                                    project.project_id
                                  )}
                                  onChange={(e) => {
                                    e.stopPropagation(); // prevent checkbox click from bubbling to <li>
                                    handleProjectCheckboxChange(
                                      e,
                                      project.project_id
                                    );
                                  }}
                                />
                                <li
                                  key={index}
                                  style={{ padding: "5px", cursor: "pointer" }}
                                  onClick={() =>
                                    handleProjectCheckboxChange(
                                      null,
                                      project.project_id
                                    )
                                  }
                                >
                                  {project.projectno}-{project.name}
                                </li>
                              </div>
                            ))}
                        </ul>
                      ) : (
                        "No projects assigned"
                      )}
                      <button
                        className="add-projects-btn"
                        onClick={() =>
                          handleBulkUpdate(user.id, selectedProjects)
                        }
                      >
                        Add Projects
                      </button>
                    </div>
                  ) : (
                    <div>
                      <p>Working Projects :</p>
                      {user.projects.length > 0 ? (
                        <ul className="working-projects-ul">
                          {user.projects.map((project, index) => (
                            <li key={index} style={{ padding: "5px" }}>
                              {project.number}-{project.name}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        "No projects assigned"
                      )}
                    </div>
                  )}
                </td>
              </tr>
            </tbody>
          </table>
          <div style={{ marginTop: "20px" }}>
            {isEditing ? (
              <>
                <button
                  onClick={handleSave}
                  style={{ marginRight: "3px", marginLeft: "2px" }}
                  className="add-btn"
                >
                  Save
                </button>
                <button
                  onClick={() => setIsEditing(false)}
                  className="cancel-btn"
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                onClick={() => setIsEditing(true)}
                className="add-btn"
                style={{ marginLeft: "2px" }}
              >
                Edit
              </button>
            )}
          </div>
        </div>
      )}
      {pictureDial && (
        <div className="Upload-Profile-Picture-dial">
          <div className="Upload-Profile-Picture">
            <div className="Upload-Profile-header">
              <h3 className="password-header">Upload Profile Picture</h3>
            </div>
            <form onSubmit={handleUploadPicture} className="picture-form">
              <label className="password-element">
                Select Profile Picture:
              </label>
              <input
                type="file"
                accept="image/*"
                className="upload-input"
                onChange={handlePictureChange}
                required
              />
              {previewPicture && (
                <img
                  src={previewPicture}
                  alt="Preview"
                  className="preview-picture"
                />
              )}
              <div
                className={
                  !previewPicture
                    ? "picture-btn-container"
                    : "picture-btn-container-2"
                }
              >
                <button className="add-btn upload-btn" type="submit">
                  Upload
                </button>
                <button
                  className="cancel-btn"
                  onClick={() => {
                    setPreviewPicture(null);
                    setPictureDial(false);
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDetails;
